import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { media } from '@mlb/react-patterns/style/helpers/media.ts';
import Button from '@mlb/react-patterns/components/Button/button.tsx';
import COLORS from '@mlb/react-patterns/style/universal.color';
import FONTS from '@mlb/react-patterns/style/fonts.ts';

import { Tracking } from 'bdvp-tracking';
import { DateUtils } from 'bdvp-common/src/utils';

import {
    useMediaConfigReel,
    useDictionary,
} from 'bdvp-common/src/hooks/config';

const ViewReels = styled(Link)`
    font-family: ${FONTS.primaryFont};
    font-size: 15px;
    font-weight: bold;
`;

const Container = styled.div`
    display: flex;
    align-items: center;
    padding: 21px;
    flex-direction: column;
    box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.5);
    flex: 0 0 146px;

    ${media.mdmin`
        padding: 18px;
        flex: 0 0 auto;
        flex-direction: row;
        box-shadow: none;
    `}

    ${ViewReels} {
        color: ${COLORS.linkBlueOnDark};

        &:hover {
            color: ${COLORS.linkBlueOnDarkHover};
        }
    }
`;

const Box = styled.div`
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: flex-end;

    ${media.mdmin`
        align-items: center;

        &:nth-child(2) {
            order: -1;
            justify-content: flex-start;
        }
        &:nth-child(3) {
            justify-content: flex-end;
        }
    `}
`;

export const CreateReel = styled(Button).attrs((({ label, onClick }) => ({
    size: 'md',
    label: label,
    styleName: 'link-blue',
    handlers: {
        onClick,
    },
})))`
    min-width: 174px;
    margin: 0 70px;
    align-items: center;

    ${media.mdmin`
        min-width: 120px;
    `}
`;

const Metadata = styled.div`
    font-size: 16px;
    white-space: nowrap;
`;

export const Count = styled.span`
    font-weight: bold;
    color: ${({ isFull }) => isFull ? COLORS.liveYellow : COLORS.mlbWhite};
    margin-right: 5px;
`;

const Duration = styled.span`
    color: ${COLORS.mlbGrey3};
`;

const Footer = (props) => {

    const {
        reel,
        viewReelsRef,
        openReelCreationModal,
        isLoggedIn,
        userId,
        closeReelCreation,
    } = props;

    const config = useMediaConfigReel();
    const { creation = {} } = config;
    const {
        create_reel_btn: createReelBtn = 'Create Reel',
        out_of: outOf = 'out of',
        view_your_reels: viewYourReels = 'View Your Reels',
    } = useDictionary();

    const numberOfClips = reel.reelClips.length;
    const maxClips = creation.maxClips ?? 5;
    const formattedDuration = `(${DateUtils.formatSecondsToTime((reel.duration || 0))})`;

    return (
        <Container>
            <Box>
                <CreateReel
                    disabled={numberOfClips < 1}
                    label={createReelBtn}
                    onClick={() => {

                        openReelCreationModal();
                        Tracking.trackReelCreationCreateReelClick();

                    }}
                />
            </Box>
            <Box>
                <Metadata>
                    <Count isFull={numberOfClips >= maxClips} >{`${numberOfClips} ${outOf} ${maxClips} videos`}</Count>
                    <Duration>{formattedDuration}</Duration>
                </Metadata>
            </Box>
            <Box>
                {isLoggedIn
                    && <ViewReels
                        to={`/${userId}/reels`}
                        ref={viewReelsRef}
                        onClick={closeReelCreation}
                    >{viewYourReels}
                    </ViewReels>
                }
            </Box>

        </Container>
    );

};

Footer.propTypes = {
    openReelCreationModal: PropTypes.func.isRequired,
    reel: PropTypes.object.isRequired,
    viewReelsRef: PropTypes.object,
    isLoggedIn: PropTypes.bool.isRequired,
    userId: PropTypes.string.isRequired,
};

Footer.defaultProps = {
    viewReelsRef: null,
};

export default Footer;
